import React, { useMemo } from 'react';
import { AgGridTable } from 'components/common';
import SoftBadge from 'components/common/SoftBadge';
import {
  MdBusiness,
  MdContacts,
  MdPhone
} from 'react-icons/md';
import { formatPhoneNumberInternational } from 'helpers/phoneNumber';
import { FaProjectDiagram } from 'react-icons/fa';

import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { LuWorkflow } from 'react-icons/lu';
import OplaCrmLink from 'components/app/crm/shared/OplaCrmLink';

// ─── small helpers ────────────────────────────────────────────────────────────

const InfoRow = ({ label, children }) => (
  <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-start">
    <div className="tw-col-span-4">
      <p className="tw-font-semibold tw-text-sm tw-mb-0 tw-text-slate-600">{label}</p>
    </div>
    <div className="tw-col-span-8">
      <div className="tw-text-sm">{children || <span className="tw-text-slate-400">—</span>}</div>
    </div>
  </div>
);

const SectionTitle = ({ icon: Icon, title }) => (
  <h6
    className="tw-font-bold tw-text-slate-600 tw-mb-3 tw-pb-1 tw-border-b tw-border-solid tw-border-slate-200 tw-uppercase"
  >
    {Icon && <Icon className="tw-mr-1" />}{title}
  </h6>
);

// ─── component ────────────────────────────────────────────────────────────────

const ContactDetailInfo = ({ contact }) => {
  const projects = contact.projects || [];

  const projectColumns = useMemo(() => [
    {
      field: 'pcv_number',
      headerName: 'PCV #',
      width: 110,
      filter: 'agTextColumnFilter'
    },
    {
      field: 'name',
      headerName: 'Project name',
      flex: 2,
      filter: 'agTextColumnFilter',
      cellRenderer: params => (
        <Link to={`/crm/projects/${params.data.id}`} target="_blank" className="tw-font-semibold tw-text-blue-600">
          {params.data.name}
        </Link>
      )
    },
    {
      field: 'q_number',
      headerName: 'Quote Number',
      width: 160,
      filter: 'agTextColumnFilter',
      valueGetter: params => [params.data.q_number, params.data.j_number].filter(Boolean).join(' '),
      cellRenderer: params =>
        {params.data.q_number && <span className="tw-mr-1 tw-text-slate-600">{params.data.q_number}</span>}
    },
    {
      field: 'j_number',
      headerName: 'P/J Number',
      width: 160,
      filter: 'agTextColumnFilter',
      valueGetter: params => [params.data.q_number, params.data.j_number].filter(Boolean).join(' '),
      cellRenderer: params =>
        {params.data.j_number && <SoftBadge bg="success">{params.data.j_number}</SoftBadge>}
    },
    {
      field: 'pj_location',
      headerName: 'Location',
      flex: 1,
      filter: 'agTextColumnFilter'
    },
    {
      field: 'sales_pipeline',
      headerName: 'Status',
      width: 150,
      filter: 'agTextColumnFilter',
      valueGetter: params => params.data.sales_pipeline?.name || '',
      cellRenderer: params => params.data.sales_pipeline?.name ? (
        <SoftBadge bg="info">{params.data.sales_pipeline.name}</SoftBadge>
      ) : null
    }
  ], []);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">

      {/* ── Identity & Contact ── */}
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-100 tw-shadow-sm tw-p-5">
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6">
          {/* Identity */}
          <div>
            <p className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-400 tw-mb-3 tw-flex tw-items-center tw-gap-1.5">
              <MdContacts size={12} /> Identity
            </p>
            <InfoRow label="First name">{contact.first_name}</InfoRow>
            <InfoRow label="Last name">{contact.last_name}</InfoRow>
            <InfoRow label="Department">{contact.department}</InfoRow>
            <InfoRow label="Buying roles">
              {contact.buying_roles && (
                <span className="tw-text-xs tw-font-medium tw-bg-slate-100 tw-text-slate-600 tw-px-2 tw-py-0.5 tw-rounded-xl">
                  {contact.buying_roles}
                </span>
              )}
            </InfoRow>
            <InfoRow label="Date of birth">{contact.dateofbirth}</InfoRow>
          </div>

          {/* Contact */}
          <div>
            <p className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-400 tw-mb-3 tw-flex tw-items-center tw-gap-1.5">
              <MdPhone size={12} /> Contact
            </p>
            <InfoRow label="Email">
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="tw-text-blue-600 hover:tw-underline">
                  {contact.email}
                </a>
              )}
            </InfoRow>
            <InfoRow label="Mobile">
              {contact.mobile && (
                <a href={`tel:${contact.mobile}`} className="tw-text-blue-600 hover:tw-underline">
                  {formatPhoneNumberInternational(contact.mobile) || contact.mobile}
                </a>
              )}
            </InfoRow>
            <InfoRow label="Main contact">{contact.main_contact}</InfoRow>
            <InfoRow label="Secondary">
              {contact.secondary_contact?.length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-1">
                  {contact.secondary_contact.map((name, i) => (
                    <span key={i} className="tw-text-xs tw-font-semibold tw-bg-blue-50 tw-text-blue-600 tw-border tw-border-blue-200 tw-px-2 tw-py-0.5 tw-rounded-xl">
                      {name}
                    </span>
                  ))}
                </div>
              )}
            </InfoRow>
            <InfoRow label="Messenger">{contact.messenger}</InfoRow>
            <InfoRow label="Other contact">{contact.other_contact}</InfoRow>
          </div>
        </div>
      </div>

      {/* ── Details ── */}
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-100 tw-shadow-sm tw-p-5">
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6">
          {/* Company */}
          <div>
            <p className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-400 tw-mb-3 tw-flex tw-items-center tw-gap-1.5">
              <MdBusiness size={12} /> Company
            </p>
            <InfoRow label="Company">
              {contact.company?.id && (
                <Link to={`/crm/customers/${contact.company.id}`} target="_blank" className="tw-text-blue-600 hover:tw-underline">
                  {contact.company.name}
                </Link>
              )}
            </InfoRow>
            <InfoRow label="Lead">
              {contact.lead?.id && (
                <Link to={`/crm/leads/${contact.lead.id}`} target="_blank" className="tw-text-blue-600 hover:tw-underline">
                  {contact.lead.name}
                </Link>
              )}
            </InfoRow>
            <InfoRow label="Sales unit">
              {contact.sales_unit?.name && (
                <span className="tw-text-slate-700">
                  {contact.sales_unit.name}
                  {contact.sales_unit.description && (
                    <span className="tw-text-slate-400"> · {contact.sales_unit.description}</span>
                  )}
                </span>
              )}
            </InfoRow>
            <InfoRow label="Address">{contact.address_name}</InfoRow>
            <InfoRow label="Country">{contact.country}</InfoRow>
            <InfoRow label="Personal page">
              {contact.personal_page && (
                <a href={contact.personal_page} target="_blank" rel="noreferrer"
                  className="tw-text-blue-600 hover:tw-underline tw-truncate tw-block tw-max-w-[200px]">
                  {contact.personal_page}
                </a>
              )}
            </InfoRow>
          </div>

          {/* Opla CRM */}
          <div>
            <p className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-400 tw-mb-3 tw-flex tw-items-center tw-gap-1.5">
              <LuWorkflow size={12} /> Opla CRM
            </p>
            <InfoRow label="Opla ID">
              {contact.opla_id && <OplaCrmLink type="contact" oplaId={contact.opla_id} />}
            </InfoRow>
          </div>

          {/* History */}
          <div>
            <p className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-400 tw-mb-3">
              History
            </p>
            <InfoRow label="Created by">{contact.created_by}</InfoRow>
            <InfoRow label="Created date">{contact.created_at}</InfoRow>
            <InfoRow label="Updated date">{contact.updated_at}</InfoRow>
          </div>
        </div>
      </div>

      {/* ── Projects ── */}
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-100 tw-shadow-sm tw-p-5">
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
          <p className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-400 tw-mb-0 tw-flex tw-items-center tw-gap-1.5">
            <FaProjectDiagram size={11} className="tw-text-blue-500" /> Projects
          </p>
          {projects.length > 0 && (
            <span className="tw-text-[11px] tw-font-bold tw-bg-slate-100 tw-text-slate-600 tw-px-2 tw-py-0.5 tw-rounded-xl">
              {projects.length}
            </span>
          )}
        </div>
        {projects.length === 0 ? (
          <p className="tw-text-sm tw-text-slate-400 tw-text-center tw-py-6">No projects linked to this contact.</p>
        ) : (
          <AgGridTable
            columnDefs={projectColumns}
            rowData={projects}
            height="400px"
            sort filter exportToCSV
          />
        )}
      </div>
    </div>
  );
};

ContactDetailInfo.propTypes = {
  contact: PropTypes.object.isRequired
};

InfoRow.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node
};

SectionTitle.propTypes = {
  icon: PropTypes.elementType,
  title: PropTypes.string.isRequired
};

export default ContactDetailInfo;
